import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../../api/client";

const RATES_KEY = ["teacher-rates"];
const PROFILE_TYPES_KEY = ["profile-types"];

function useTeacherRates() {
  return useQuery({
    queryKey: RATES_KEY,
    queryFn: () => apiClient.get("/api/v1/teacher-rates/").then((r) => r.data),
  });
}

function useCreateTeacherRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiClient.post("/api/v1/teacher-rates/", body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: RATES_KEY }),
  });
}

function useUpdateTeacherRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) =>
      apiClient
        .patch(`/api/v1/teacher-rates/${id}/`, body)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: RATES_KEY }),
  });
}

function useDeleteTeacherRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/api/v1/teacher-rates/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: RATES_KEY }),
  });
}

// ── Tipos de perfil de la academia (catálogo que el OWNER puede ampliar) ─────
function useProfileTypes() {
  return useQuery({
    queryKey: PROFILE_TYPES_KEY,
    queryFn: () =>
      apiClient.get("/api/v1/profile-types/").then((r) => {
        const d = r.data;
        return Array.isArray(d) ? d : d.results ?? [];
      }),
  });
}

// Crea un tipo de perfil nuevo (p. ej. "Coordinador") desde el formulario de
// tarifas — POST /api/v1/profile-types/. `company` la fija el backend desde la
// membresía OWNER; aquí solo se envía el nombre.
function useCreateProfileType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name) =>
      apiClient
        .post("/api/v1/profile-types/", { name })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROFILE_TYPES_KEY }),
  });
}

// Renombra un tipo de perfil — PATCH /api/v1/profile-types/<id>/.
function useUpdateProfileType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) =>
      apiClient
        .patch(`/api/v1/profile-types/${id}/`, body)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PROFILE_TYPES_KEY });
      qc.invalidateQueries({ queryKey: RATES_KEY }); // el display de la tarifa cambia
    },
  });
}

// Elimina un tipo de perfil — DELETE /api/v1/profile-types/<id>/. El backend
// responde 409 si algún docente todavía lo tiene asignado (Profile.type).
function useDeleteProfileType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/api/v1/profile-types/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: PROFILE_TYPES_KEY }),
  });
}

// Loads teacher options for react-select from the memberships endpoint (TASK-06).
// Returns [] gracefully if the endpoint is not yet available.
async function loadTeacherOptions(inputValue) {
  try {
    const { data } = await apiClient.get(
      `/api/v1/memberships/?role=TEACHER&search=${encodeURIComponent(inputValue)}`
    );
    const list = Array.isArray(data) ? data : data.results ?? [];
    // TeacherRate.teacher is a Profile FK → use the profile id as the value.
    // The label comes from the nested user object (full_name → email fallback).
    return list.map((m) => ({
      value: m.profile ?? m.user?.id ?? m.id,
      label:
        m.user?.full_name ||
        m.user?.email ||
        `Docente ${m.profile ?? m.id}`,
    }));
  } catch {
    return [];
  }
}

// Opción fija de "no aplica" (tarifa específica de un docente, sin tipo de
// perfil por defecto) — no viene del backend, es una elección del formulario.
const NO_APLICA_OPTION = { value: null, label: "No aplica" };

// Carga los tipos de perfil reales de la academia (Profesional/Practicante,
// sembrados por users.services.seed_default_profile_types) para
// TeacherRateForm. Antes el selector usaba ids fijos (1/2) que asumían
// fixtures globales que nunca existieron — cualquier tarifa por tipo de
// perfil tronaba con 400 "Invalid pk". Grave silenciosa si el endpoint
// falla: no bloquea el formulario, solo deja "No aplica" como única opción.
async function loadProfileTypeOptions() {
  try {
    const { data } = await apiClient.get("/api/v1/profile-types/");
    const list = Array.isArray(data) ? data : data.results ?? [];
    return [NO_APLICA_OPTION, ...list.map((t) => ({ value: t.id, label: t.name }))];
  } catch {
    return [NO_APLICA_OPTION];
  }
}

export {
  useTeacherRates,
  useCreateTeacherRate,
  useUpdateTeacherRate,
  useDeleteTeacherRate,
  useProfileTypes,
  useCreateProfileType,
  useUpdateProfileType,
  useDeleteProfileType,
  loadTeacherOptions,
  loadProfileTypeOptions,
  NO_APLICA_OPTION,
  RATES_KEY,
  PROFILE_TYPES_KEY,
};
